"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { Info } from "lucide-react"
import { useI18n } from "@/i18n"
import type { PlanResponse } from "@bitebalance/contracts"

/**
 * ADR-051: the current plan was generated under an earlier calorie target
 * (plan_revisions.goal_snapshot vs the profile's current target) and a full
 * rebuild has not replaced it — e.g. the rebuild was infeasible within the
 * budget. Informational, calm, and distinct from RegenFailedBanner (which
 * carries the failed job's own error and actions): this notice explains WHY
 * untouched days may not match the new target and that changed meals are
 * matched to it. No internal codes, no day indexes.
 */
export function SettingsDriftNotice({ drift }: { drift: NonNullable<PlanResponse["settingsDrift"]> }) {
  const { t, locale } = useI18n()
  const kcal = (n: number) => new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-DE").format(n)
  return (
    <div
      role="status"
      className="flex gap-3 rounded-bb-lg border border-border bg-surface-2 px-4 py-3 text-sm text-ink"
    >
      <Info className="mt-0.5 size-4 shrink-0 text-ink-2" aria-hidden="true" />
      <div>
        <p className="font-semibold">
          {t("views.plan.settingsDriftTitle", { planTarget: kcal(drift.planTargetKcal) })}
        </p>
        <p className="mt-1 text-ink-2">
          {t("views.plan.settingsDriftBody", { currentTarget: kcal(drift.currentTargetKcal) })}
        </p>
      </div>
    </div>
  )
}
